import { useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import querystring from 'querystring';
import * as assets from 'client/utils/assets';
import useVehicleModels, { VehicleModel } from './queries/useVehicleModels';
import useVehicleOptions, { VehicleOption } from './queries/useVehicleOptions';
import { ModelOptions } from '../pages/types/ModelOptions';
import useVehicleModelDetails, {
  VehicleModelDetails
} from './queries/useVehicleModelDetails';
import PATHS from 'client/utils/paths';
import useOrderDetail, { VehicleConfigurationItem } from './queries/useOrderDetail';
import useUser from './queries/useUser';

// Populate default params as needed
// Return default in case the provided param is invalid
const getSelectedOptionCodes = (
  location,
  vehicleModelDetails?: VehicleModelDetails,
  vehicleConfigurationItems?: VehicleConfigurationItem[],
) => {
  // TODO: Replace with URLSearchParams + polyfill
  const params = querystring.parse(location.search.replace(/\?/g, ''));
  const exteriorDefault = vehicleConfigurationItems?.find(i => i.vehicleOption?.vehicleOptionType?.categoryCode === 'EXC')
    ?.vehicleOption?.vehicleOptionType?.typeCode ||
    vehicleModelDetails?.vehicleConfigurationAssets?.exterior?.color
      ?.typeCodes?.[0];
  const interiorDefault = vehicleConfigurationItems?.find(i => i.vehicleOption?.vehicleOptionType?.categoryCode === 'INT')
    ?.vehicleOption?.vehicleOptionType?.typeCode ||
    vehicleModelDetails?.vehicleConfigurationAssets?.interior?.color
      ?.typeCodes?.[0];
  const wheelDefault =vehicleConfigurationItems?.find(i => i.vehicleOption?.vehicleOptionType?.categoryCode === 'WHL')
    ?.vehicleOption?.vehicleOptionType?.typeCode ||
    vehicleModelDetails?.vehicleConfigurationAssets?.wheel?.typeCodes?.[0];

  return {
    EXC: {
      value: params.EXC || exteriorDefault,
      default: exteriorDefault
    },
    INT: {
      value: params.INT || interiorDefault,
      default: interiorDefault
    },
    WHL: {
      value: params.WHL || wheelDefault,
      default: wheelDefault
    }
  };
};
/**
 * - Parses the querystring to determine the codes for user's selected model and options
 * - Gets the full model list (get model id)
 * - Gets the selected model's options (get option ids)
 */
// TODO: reuse useConfigurationSelectionV2 in existing useConfigurationSelection
export default function useConfigurationSelectionV2() {
  // location can be used as a hook dependency
  const location = useLocation();
  const history = useHistory();
  const { region } = useParams<{ region: string }>();
  // TODO: Replace with URLSearchParams + polyfill
  const params = querystring.parse(location.search.replace(/\?/g, ''));

  const modelCode = params.VEH as string;
  const orderNum = params.ORDER as string;

  let selectedModel: VehicleModel | undefined;
  const [selectedVehicleOptions, setSelectedVehicleOptions] = useState<
    VehicleOption[] | undefined
  >();

  // Split model code/edition, if applicable
  const { vehicleModels, loading: loadingVehicleModels } = useVehicleModels();

  const { user, loading: loadingUser } = useUser();
  const { order, loading: loadingOrder } = useOrderDetail(orderNum, {
    skip: !!modelCode || !orderNum?.length || !user
  });

  if (!loadingVehicleModels && !loadingUser && !loadingOrder) {
    let selectedModelCode;
    if(modelCode) {
      selectedModelCode = modelCode;
    }else {
      if(order){
        selectedModelCode = order.vehicleModel?.fullModelCode;
      }else{
        if(orderNum){
          // if orderNumber is not correct redirect to model selection page
          history.push(`/${region}${PATHS.DESIGN}`);
        }
      }

      if(!selectedModelCode){
        selectedModelCode = 'FF91FNA-ALLIANCE';
      }
    }

    // Get selected model from vehicle models list
    selectedModel = vehicleModels?.find(
      (x) => x.fullModelCode === selectedModelCode.toUpperCase()
    );

    // redirect back to model selection page if modelCode is not correct
    if (!selectedModel) {
      history.push(`/${region}${PATHS.DESIGN}`);
    }
  }

  // Skip query until dependencies are ready
  const { vehicleModelDetails, loading: loadingVehicleModelDetails } =
    useVehicleModelDetails(selectedModel?.id || 0, {
      skip: !selectedModel?.id
    });
  const { vehicleOptions, loading: loadingVehicleOptions } = useVehicleOptions(
    selectedModel?.id || 0,
    {
      skip: !selectedModel?.id
    }
  );

  const [modelOptions, setModelOptions] = useState<ModelOptions>();
  const [configurationSelectionSummary, setConfigurationSelectionSummary] = useState<{basePrice: number, subtotal: number}>();

  // Filter selected options
  useEffect(() => {
    const updateSelectedOptions = async () => {
      if (
        !loadingVehicleOptions &&
        vehicleOptions &&
        !loadingVehicleModelDetails &&
        vehicleModelDetails &&
        !loadingOrder
      ) {
        order?.vehicleConfigurationCore.vehicleConfigurationItems
        const codes = getSelectedOptionCodes(location, vehicleModelDetails, order?.vehicleConfigurationCore?.vehicleConfigurationItems);
        const options: VehicleOption[] = [];
        const validatedCodes: { [key: string]: string } = {};
        let subtotal = vehicleModelDetails.basePrice;
        Object.keys(codes).forEach((key) => {
          // Filter options by code to get selected option
          let option = vehicleOptions.find(
            (o) => codes[key].value === o.vehicleOptionType.typeCode
          );
          let validatedCode = codes[key].value;

          if (!option) {
            // Use default code if provided code is invalid
            option = vehicleOptions.find(
              (o) => codes[key].default === o.vehicleOptionType.typeCode
            );
            validatedCode = codes[key].default;
          }

          option = option as VehicleOption;
          subtotal += option.price;
          options.push(option);
          validatedCodes[key] = validatedCode;
        });
        setSelectedVehicleOptions(options);
        setModelOptions(assets.mapToModelOptionsProps(vehicleModelDetails));
        setConfigurationSelectionSummary({subtotal, basePrice: vehicleModelDetails.basePrice});
      }
    };

    updateSelectedOptions();
  }, [
    loadingVehicleOptions,
    vehicleOptions,
    location,
    loadingVehicleModelDetails,
    vehicleModelDetails,
    loadingOrder,
    order,
  ]);

  return {
    vehicleModels,
    selectedModel,
    vehicleOptions,
    modelOptions,
    selectedVehicleOptions,
    configurationSelectionSummary,
    loading:
      loadingOrder ||
      loadingUser ||
      loadingVehicleModels ||
      loadingVehicleOptions ||
      loadingVehicleModelDetails ||
      !configurationSelectionSummary
  };
}
